// Chakra imports
import {
  Box,
  Text,
  useColorModeValue,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Divider,
  Icon,
  Badge,
} from "@chakra-ui/react";
import React, { useState, useEffect } from "react";
import { MdEmail, MdPerson, MdPhone } from "react-icons/md";
import { formatDate } from "utils/format";
import supportService from "services/supportService";

export default function Mails() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const textColorSecondary = useColorModeValue("secondaryGray.600", "white");
  const cardBg = useColorModeValue("white", "navy.800");
  const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.100");
  const iconBoxBg = useColorModeValue("brand.50", "whiteAlpha.100");
  const rowBorder = useColorModeValue("gray.100", "whiteAlpha.100");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 50, total: 0, count: 0 });

  useEffect(() => {
    const fetchMessages = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await supportService.getContactMessages({ skip: 0, limit: 50 });
        if (result.success && result.data) {
          setMessages(result.data);
          if (result.pagination) {
            setPagination({
              skip: result.pagination.skip,
              limit: result.pagination.limit,
              total: result.pagination.total,
              count: result.pagination.count,
            });
          }
        } else {
          setError(result.error || "Failed to load contact messages");
        }
      } catch (err) {
        console.error("Error fetching contact messages:", err);
        setError("Failed to load contact messages. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, []);

  if (loading) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Box display="flex" justifyContent="center" alignItems="center" minH="400px">
          <Spinner size="xl" color="brand.500" />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box pt={{ base: "130px", md: "80px", xl: "80px" }}>
        <Alert status="error" borderRadius="15px">
          <AlertIcon />
          <Text color={textColor}>{error}</Text>
        </Alert>
      </Box>
    );
  }

  return (
    <Box pt={{ base: "130px", md: "80px", xl: "80px" }} maxW="1000px" mx="auto">
      <Flex direction="column" gap="6">
        <Flex align="center" gap="3">
          <Flex
            w="12"
            h="12"
            borderRadius="xl"
            bg={iconBoxBg}
            align="center"
            justify="center"
          >
            <Icon as={MdEmail} color="brand.500" boxSize="6" />
          </Flex>
          <Box>
            <Text color={textColor} fontSize="2xl" fontWeight="700">
              Mails
            </Text>
            <Text color={textColorSecondary} fontSize="sm">
              {pagination.total} contact message{pagination.total !== 1 ? "s" : ""}
            </Text>
          </Box>
        </Flex>

        <Card p="0" bg={cardBg} overflow="hidden" borderRadius="16px" boxShadow="sm">
          <Flex align="center" gap="3" px="6" py="4" borderBottomWidth="1px" borderColor={borderColor}>
            <Icon as={MdEmail} color="brand.500" boxSize="5" />
            <Text color={textColor} fontSize="lg" fontWeight="600">
              Contact messages
            </Text>
          </Flex>
          <Divider borderColor={borderColor} />

          {messages && messages.length > 0 ? (
            <Box py="2">
              {messages.map((msg) => (
                <Box
                  key={msg.id}
                  px="6"
                  py="4"
                  borderBottomWidth="1px"
                  borderColor={rowBorder}
                  _last={{ borderBottom: "none" }}
                >
                  <Flex align="center" gap="2" flexWrap="wrap" mb="2">
                    <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                      {msg.inquiry_type || "—"}
                    </Badge>
                    {msg.marked_spam && (
                      <Badge colorScheme="red" fontSize="xs">
                        Spam
                      </Badge>
                    )}
                  </Flex>
                  <Text color={textColor} fontSize="md" fontWeight="600" mb="1">
                    {msg.subject || "No subject"}
                  </Text>
                  <Text color={textColorSecondary} fontSize="sm" noOfLines={2} mb="2">
                    {msg.message}
                  </Text>
                  <Flex align="center" gap="4" flexWrap="wrap" fontSize="sm">
                    <Flex align="center" gap="1">
                      <Icon as={MdPerson} boxSize="4" color={textColorSecondary} />
                      <Text color={textColor}>
                        {[msg.first_name, msg.last_name].filter(Boolean).join(" ") || "—"}
                      </Text>
                    </Flex>
                    <Text color={textColorSecondary}>{msg.email || "—"}</Text>
                    {msg.phone && (
                      <Flex align="center" gap="1">
                        <Icon as={MdPhone} boxSize="4" color={textColorSecondary} />
                        <Text color={textColorSecondary}>{msg.phone}</Text>
                      </Flex>
                    )}
                    <Text color={textColorSecondary} fontSize="xs">
                      {msg.created_at ? formatDate(msg.created_at) : "—"}
                    </Text>
                  </Flex>
                </Box>
              ))}
            </Box>
          ) : (
            <Flex direction="column" align="center" justify="center" py="16" px="6">
              <Text color={textColorSecondary} fontSize="sm" textAlign="center">
                No contact messages yet.
              </Text>
            </Flex>
          )}
        </Card>
      </Flex>
    </Box>
  );
}
